/// <amd-module name="Tfs/Frame/Components/Navigation/ProjectNavigation" />
import * as React from "react";
import * as Resources from "Tfs/Frame/Resources";
import { isNavigationItemBuiltinOrTrusted, ITfsNavigationService, NavigationDisplayMode } from "Tfs/Platform/Navigation";
import { ITfsPageService } from "Tfs/Platform/Page";
import { ProjectPersona } from "Tfs/Platform/ProjectPersona";
import {
    IObservableArrayEventArgs,
    IObservableValue,
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    ObservableArray,
    ObservableCollection,
    ObservableValue
} from "VSS/Core/Observable";
import { ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { localeIgnoreCaseComparer, format } from "VSS/Core/Util/String";
import { ContributedIcon } from "VSS/Features/PlatformUI/ContributedIcon";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FastPageSwitchEvent, FastPageSwitchEventDetail } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, IVssLayoutManager, useComponentContext, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IContributedNavigation, IVssNavigationService, NavigationFlags } from "VSS/Platform/Navigation";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { Callout } from "VSSUI/Callout";
import { ExpandableContainer, IExpandable } from "VSSUI/Expandable";
import { FocusGroupContext } from "VSSUI/FocusGroup";
import { FocusZone, FocusZoneContext, FocusZoneDirection, FocusZoneKeyStroke } from "VSSUI/FocusZone";
import { Icon, IconSize } from "VSSUI/Icon";
import { ContextualMenu, IMenuItem, IMenuItemDetails, IMenuProps, MenuCell, MenuItemType } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId, KeyCode } from "VSSUI/Util";
import { getTabIndex } from "VSSUI/Utilities/Focus";
import { Location } from "VSSUI/Utilities/Position";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";
import { useFlyoutNavigation } from "../../Util/Navigation";
import { SuiteLogo } from "../SuiteLogo/SuiteLogo";
import { SwitchToDesktopButton } from "./SwitchToDesktopButton";

const enum Constants {
    defaultHubOrder = 100,
    hubGroupNavItemHeight = 48,
    hubNavItemHeight = 40,

    headerHeight = 48,
    createButtonHeight = 48,
    projectItemHeight = 48,
    projectSettingsHeight = 48,
    expandCollapseHeight = 48,

    separatorHeight = 1,
    navItemsMargin = 4,
    footerHeight = 22
}

interface IProjectNavigationProps {
    isCallout?: boolean;
    collapse?: boolean;
    onToggleExpand?: () => void;
}

class ProjectNavigation extends VssComponent<IProjectNavigationProps> {
    private navListRef = React.createRef<HTMLDivElement>();
    private displayedHubGroupCollectionId: string;
    private displayedHubGroupId: string;
    private displayedHubId: string;

    private expandedHubGroup: string;

    private hubGroups: IContributedNavigation[];
    private showHubs: ObservableValue<boolean>;
    private hubs: IContributedNavigation[];

    private fixedElementHeight: number;
    private navigationHeight: number;

    private userIsMember: boolean;
    private settingsUrl?: string;
    private projectName: string;
    private projectImageUrl?: string;
    private expanded: IReadonlyObservableValue<NavigationDisplayMode>;

    private touchMoveCounter: number;

    constructor(props: IProjectNavigationProps, context: IVssComponentContext) {
        super(props);

        // Save the observable expanded nature of the logo in our component state.
        const navService = context.pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
        this.expanded = navService.getNavigationDisplayMode();

        const tfsPageService = context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = tfsPageService.getData();

        if (pageData && pageData.project) {
            this.projectName = pageData.project.name;
            this.projectImageUrl = pageData.project.imageUrl;
            this.settingsUrl = routeUrl(context.pageContext, "ms.vss-admin-web.project-admin-hub-route", { project: this.projectName });
        }

        this.userIsMember = userHasClaim(context.pageContext, UserClaims.Member);

        // We need to compute the amount of space available and determine if we will
        // show the hubs or not.
        const layoutManager = context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        const footerComponents = layoutManager.getComponentsForRegion("footer");

        // Compute the amount of space used by the fixed navigation elements when the
        // navigation is in collapsed display mode.
        this.fixedElementHeight =
            Constants.headerHeight +
            Constants.projectItemHeight +
            Constants.expandCollapseHeight +
            (footerComponents && footerComponents.length > 0 ? Constants.footerHeight : 0) +
            2 * Constants.separatorHeight +
            2 * Constants.navItemsMargin +
            (this.userIsMember ? Constants.createButtonHeight : 0) +
            (this.settingsUrl ? Constants.projectSettingsHeight : 0);

        // Process the navigation contributions into the set we need to display.
        const navigationService = context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const displayedNavigation = navigationService.getDisplayedNavigation();

        // Initialize the displayed navigation ids.
        this.displayedHubGroupCollectionId = displayedNavigation[1];
        this.displayedHubGroupId = displayedNavigation[2];
        this.displayedHubId = displayedNavigation[3];

        this.expandedHubGroup = displayedNavigation[2];

        // Build the set of navigation elements we are showing.
        this.buildNavigationLists(navigationService, this.displayedHubGroupCollectionId, this.displayedHubGroupId);

        // Determine the maximum number of hubs within a hubGroup.
        let maxHubsForHubGroup = 0;
        for (const hubGroup of this.hubGroups) {
            maxHubsForHubGroup = Math.max(maxHubsForHubGroup, hubGroup.children!.length);
        }

        // We need to compute the height of the items that could be displayed.
        this.navigationHeight = Constants.hubGroupNavItemHeight * this.hubGroups.length + Constants.hubNavItemHeight * maxHubsForHubGroup;

        // Determine whether or not the hubs should be visible on the initial render.
        this.showHubs = new ObservableValue(this.checkAvailableNavigationHeight(this.expanded.value));
    }

    public render(): JSX.Element {
        const { collapse, isCallout, onToggleExpand } = this.props;

        return (
            <Observer expanded={{ filter: this.onExpandedChange, observableValue: this.expanded }} showHubs={this.showHubs}>
                {(props: { expanded: NavigationDisplayMode; showHubs: boolean }) => {
                    let expandClass: string;
                    let chevronText: string;
                    let chevron: string;

                    if (collapse === false || (collapse === undefined && props.expanded === NavigationDisplayMode.Expanded)) {
                        chevronText = Resources.ShowLess;
                        chevron = "DoubleChevronLeft";
                        expandClass = "expanded";
                    } else {
                        chevronText = Resources.ShowMore;
                        chevron = "DoubleChevronRight";
                        expandClass = "collapsed";
                    }
                    return (
                        <FocusZone
                            circularNavigation={true}
                            direction={FocusZoneDirection.Vertical}
                            focusGroupProps={{ defaultElementId: "create-action" }}
                            focusOnMount={isCallout}
                            handleTabKey={isCallout}
                            postprocessKeyStroke={() => FocusZoneKeyStroke.IgnoreParents}
                        >
                            <div
                                className={css("project-navigation region-navigation flex-column flex-grow", expandClass)}
                                data-renderedregion="navigation"
                                aria-label={format(Resources.ProjectNavigationAriaLabel, this.projectName)}
                                ref={this.navListRef}
                                role="navigation"
                                onTransitionEnd={this.onTransitionEnd}
                            >
                                {isCallout && (
                                    <div className="region-header flex-row flex-noshrink">
                                        <SuiteLogo alwaysExpanded={true} routeId="ms.vss-tfs-web.suite-me-page-route" />
                                    </div>
                                )}
                                <div className="collapsible-section flex-noshrink">
                                    <div className="project-item flex-row flex-grow flex-center scroll-hidden">
                                        <span className="navigation-icon flex-row flex-center flex-noshrink justify-center">
                                            <ProjectPersona displayName={this.projectName} size="small" url={this.projectImageUrl} />
                                        </span>
                                        <span className="font-weight-heavy expanded-only flex-grow text-ellipsis">{this.projectName}</span>
                                    </div>
                                    {this.userIsMember && this.projectName && (
                                        <ExpandableContainer
                                            ariaLabel={Resources.Create}
                                            className="focus-treatment alt-hover-effect flex-row cursor-pointer"
                                            expandableId="ms.vss-tfs-web.vertical-header-create-menu"
                                            expandKey={KeyCode.rightArrow}
                                            id="create-action"
                                            renderCallout={(expandable: IExpandable, id: string, element: HTMLElement) => {
                                                const menuItems: IMenuItem[] | IReadonlyObservableArray<IMenuItem> = getMenuItems(
                                                    this.context.pageContext,
                                                    "ms.vss-tfs-web.vertical-header-create-menu"
                                                );
                                                const observableMenuItems = menuItems as ObservableCollection<IMenuItem>;
                                                const noActionsMenuItems = new ObservableArray<IMenuItem>();
                                                noActionsMenuItems.push({
                                                    id: "no-actions-available",
                                                    disabled: true,
                                                    itemType: MenuItemType.Header,
                                                    text: Resources.NoActionsAvailable
                                                });

                                                const removeNoActionsIfItemsLoaded = (value: IObservableArrayEventArgs<IMenuItem>) => {
                                                    if (value.addedItems && value.addedItems.length > 0) {
                                                        noActionsMenuItems.value[0].hidden = true;
                                                        menuItems.unsubscribe(removeNoActionsIfItemsLoaded);
                                                    }
                                                };

                                                this.setTimeout(() => {
                                                    if (menuItems.length === 0) {
                                                        observableMenuItems.push(noActionsMenuItems);
                                                        menuItems.subscribe(removeNoActionsIfItemsLoaded);
                                                    }
                                                }, 500);

                                                return (
                                                    <ContextualMenu
                                                        anchorElement={element}
                                                        anchorOrigin={{ horizontal: Location.end, vertical: Location.start }}
                                                        menuProps={{
                                                            id,
                                                            items: menuItems
                                                        }}
                                                        menuOrigin={{ horizontal: Location.start, vertical: Location.start }}
                                                        onDismiss={() => {
                                                            expandable.collapse();
                                                            menuItems.unsubscribe(removeNoActionsIfItemsLoaded);
                                                        }}
                                                    />
                                                );
                                            }}
                                        >
                                            <Icon
                                                className="navigation-icon flex-row flex-center justify-center"
                                                iconName="Add"
                                                size={IconSize.medium}
                                            />
                                        </ExpandableContainer>
                                    )}
                                </div>
                                <div className="separator flex-row flex-noshrink" />
                                <div
                                    className={css(
                                        "navigation-section flex-column flex-grow scroll-hidden v-scroll-auto custom-scrollbar",
                                        ((props.expanded === NavigationDisplayMode.Collapsed && collapse !== false) || collapse === true) &&
                                            "custom-scrollbar-hidden"
                                    )}
                                    role="menubar"
                                    tabIndex={-1}
                                >
                                    {this.hubGroups.map((hubGroup: IContributedNavigation, index) => {
                                        return this.renderNavigation(
                                            hubGroup,
                                            true,
                                            hubGroup.id === this.expandedHubGroup && props.showHubs,
                                            index === 0
                                        );
                                    })}
                                </div>
                                <ScreenSizeConditional condition={(screenSize) => screenSize > ScreenSize.xsmall}>
                                    <div className="separator flex-row flex-noshrink" />
                                </ScreenSizeConditional>
                                <SwitchToDesktopButton
                                    className="flex-row flex-center"
                                    context={this.context}
                                    iconClassName="navigation-icon flex-row flex-center flex-noshrink justify-center cursor-pointer"
                                    textClassName="text-ellipsis"
                                />
                                <div className="collapsible-section flex-noshrink">
                                    {this.userIsMember && (
                                        <FPSLink
                                            className={css(
                                                "hub stand-alone navigation-element navigation-link focus-treatment hover-effect flex-row flex-grow flex-center scroll-hidden relative",
                                                this.displayedHubId === "ms.vss-admin-web.project-admin-hub" && "displayed"
                                            )}
                                            href={this.settingsUrl}
                                            id="settings-link"
                                            telemetrySource="vertical.settings"
                                            removeUnderline={true}
                                        >
                                            <Icon
                                                ariaLabelledBy="project-settings-text"
                                                className="navigation-icon flex-row flex-center justify-center"
                                                iconName="Settings"
                                                size={IconSize.medium}
                                            />
                                            <span className="navigation-text expanded-only text-ellipsis" id={getSafeId("project-settings-text")}>
                                                {Resources.ProjectSettings}
                                            </span>
                                        </FPSLink>
                                    )}
                                    <ScreenSizeConditional condition={(screenSize) => screenSize > ScreenSize.xsmall}>
                                        <FocusGroupContext.Consumer>
                                            {(groupContext): JSX.Element => (
                                                <FocusZoneContext.Consumer>
                                                    {(zoneContext): JSX.Element => (
                                                        <Tooltip text={chevronText}>
                                                            <div
                                                                aria-label={chevronText}
                                                                className="hub stand-alone focus-treatment alt-hover-effect flex-row"
                                                                data-focuszone={zoneContext.focuszoneId}
                                                                id="project-expansion-toggle"
                                                                onClick={onToggleExpand ? onToggleExpand : this.toggleExpansion}
                                                                onFocus={() => {
                                                                    groupContext.onFocus("project-expansion-toggle");
                                                                }}
                                                                onKeyDown={this.onKeyDownToggle}
                                                                role="button"
                                                                tabIndex={getTabIndex({ id: "project-expansion-toggle" }, groupContext)}
                                                            >
                                                                <Icon
                                                                    className="navigation-icon flex-row flex-center flex-noshrink justify-center cursor-pointer"
                                                                    iconName={chevron}
                                                                    size={IconSize.medium}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    )}
                                                </FocusZoneContext.Consumer>
                                            )}
                                        </FocusGroupContext.Consumer>
                                    </ScreenSizeConditional>
                                </div>
                            </div>
                        </FocusZone>
                    );
                }}
            </Observer>
        );
    }

    public componentDidMount() {
        super.componentDidMount();

        this.addEventListener(window, "resize", this.onResize);
        this.addEventListener(document.body, "fpsLoading", this.onFpsLoading);
    }

    private buildNavigationLists(navigationService: IVssNavigationService, hubGroupCollectionId: string, hubGroupId: string): void {
        this.hubGroups = [];
        this.hubs = [];

        // Get the set of hubGroups from the HubGroupCollection's children
        let parentNavigation = navigationService.getNavigation(hubGroupCollectionId);
        if (parentNavigation && parentNavigation.children) {
            for (const navigation of parentNavigation.children) {
                // Exclude hub groups that are hidden.
                // Exclude hub groups with no hubs.
                if (
                    ((navigation.flags || 0) & NavigationFlags.Hidden) !== NavigationFlags.Hidden &&
                    navigation.children &&
                    navigation.children.length
                ) {
                    this.hubGroups.push(navigation);
                }
            }
        }

        // Get the set of hubs from the HubGroup's children
        parentNavigation = navigationService.getNavigation(hubGroupId);
        if (parentNavigation && parentNavigation.children) {
            for (const navigation of parentNavigation.children) {
                // Exclude hubs that are hidden.
                if (((navigation.flags || 0) & NavigationFlags.Hidden) !== NavigationFlags.Hidden) {
                    this.hubs.push(navigation);
                }
            }
        }

        // Make sure the displayed hubGroups and hubs are ready for rendering.
        this.hubGroups = this.sortNavigationList(this.hubGroups);
        this.hubs = this.sortNavigationList(this.hubs);
    }

    private checkAvailableNavigationHeight = (expanded: NavigationDisplayMode): boolean => {
        if ((expanded === NavigationDisplayMode.Collapsed && this.props.collapse !== false) || this.props.collapse) {
            return window.innerHeight >= this.navigationHeight + this.fixedElementHeight;
        } else {
            return true;
        }
    };

    private getMenuProps(hubGroup: IContributedNavigation): IMenuProps {
        const menuItems: IMenuItem[] = [];

        // Add the header with the hubGroup name to the top of the menu.
        menuItems.push({ id: hubGroup.id, itemType: MenuItemType.Header, text: hubGroup.name });

        // Add each of the hubs from the hubGroup as a menuitem.
        for (let index = 0; index < hubGroup.children!.length; index++) {
            const hub = hubGroup.children![index];
            menuItems.push({
                data: hub,
                id: hub.id,
                href: hub.href,
                onActivate: this.onActivateMenuItem,
                renderMenuCell: this.renderMenuCell,
                text: hub.name
            });
        }

        return { id: "menu-" + hubGroup.id, items: menuItems };
    }

    private navigationComparer(navElement1: IContributedNavigation, navElement2: IContributedNavigation): number {
        if (navElement1.order === navElement2.order) {
            return localeIgnoreCaseComparer(navElement1.id, navElement2.id);
        } else {
            return navElement1.order - navElement2.order;
        }
    }

    private onActivateMenuItem = (menuItem: IMenuItem, event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): boolean | void => {
        return onClickFPS(this.context.pageContext, menuItem.href!, true, event);
    };

    private onExpandedChange = (expanded: NavigationDisplayMode): boolean => {
        const showHubs = this.checkAvailableNavigationHeight(expanded);
        if (showHubs !== this.showHubs.value) {
            this.showHubs.value = showHubs;
        }

        return true;
    };

    private onKeyDownToggle = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (event.which === KeyCode.space || event.which === KeyCode.enter) {
                this.props.onToggleExpand ? this.props.onToggleExpand() : this.toggleExpansion();
                event.preventDefault();
            }
        }
    };

    private onFpsLoading = (event: FastPageSwitchEvent<FastPageSwitchEventDetail>) => {
        if (event.detail && event.detail.data && event.detail.data.navigationId) {
            const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
            const newHubNavigation = navigationService.getNavigation(event.detail.data.navigationId);
            if (newHubNavigation && newHubNavigation.parentId) {
                this.displayedHubGroupId = newHubNavigation.parentId;
                this.displayedHubId = newHubNavigation.id;

                // Rebuild the navigation lists and make sure the ones we are showing are configured.
                this.buildNavigationLists(navigationService, this.displayedHubGroupCollectionId, this.displayedHubGroupId);
                this.forceUpdate();
            }
        }
    };

    private onResize = () => {
        const showHubs = this.checkAvailableNavigationHeight(this.expanded.value);
        if (showHubs !== this.showHubs.value) {
            this.showHubs.value = showHubs;
        }
    };

    private onTransitionEnd = (ev: React.TransitionEvent<HTMLDivElement>) => {
        if (this.navListRef.current === ev.target) {
            // Trigger a window resize event, allowing components to compute their new size.
            window.dispatchEvent(new CustomEvent("resize"));
        }
    };

    private renderMenuCell = (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails): JSX.Element | undefined => {
        if (menuCell === MenuCell.Icon) {
            const hub = menuItem.data as IContributedNavigation;

            return (
                <div className="bolt-menuitem-cell-content bolt-menuitem-cell-icon flex-row">
                    <ContributedIcon
                        alt=""
                        contributionId={hub.id}
                        icon={hub.icon}
                        iconAsset={hub.iconAsset}
                        iconName={hub.iconName || "ViewAll"}
                        iconSize={IconSize.medium}
                        pageContext={this.context.pageContext}
                    />
                </div>
            );
        }

        return undefined;
    };

    private renderNavigation(navigation: IContributedNavigation, hubGroup: boolean, showChildren: boolean, addTabStop?: boolean): JSX.Element {
        const className = hubGroup
            ? css("hub-group", navigation.id === this.displayedHubGroupId && "displayed")
            : css("hub", navigation.id === this.displayedHubId && "displayed");

        // Initially start with the naigvation icon and build out from there.
        let content = (
            <span className="navigation-icon flex-row flex-center flex-noshrink justify-center">
                <ContributedIcon
                    alt=""
                    contributionId={navigation.id}
                    icon={navigation.icon}
                    iconAsset={navigation.iconAsset}
                    iconName={navigation.iconName || "ViewAll"}
                    iconSize={IconSize.medium}
                    pageContext={this.context.pageContext}
                />
            </span>
        );

        // In collapsed mode the icon gets a tooltip since we cant see the name.
        // For all hubs, and hubGroups with 1 child, with > 1 it gets a menu.
        if ((this.expanded.value === NavigationDisplayMode.Collapsed || this.props.collapse) && (!hubGroup || navigation.children!.length === 1)) {
            content = <Tooltip text={navigation.name}>{content}</Tooltip>;
        }

        let expandIcon: JSX.Element | undefined = undefined;

        if (hubGroup && navigation.children!.length > 1) {
            expandIcon = (
                <ScreenSizeConditional condition={(size) => size <= ScreenSize.small}>
                    <Icon
                        className="touch-chevron justify-center flex-center"
                        iconName={showChildren ? "ChevronUp" : "ChevronDown"}
                        onClick={(e) => this.toggleExpandedHubGroup(navigation.id, e)}
                        size={IconSize.small}
                    />
                </ScreenSizeConditional>
            );
        }

        // Create the navigation element link, ensure the navigationId notes the id
        // for the hub the link will result in, not the navigation id of the link
        // you are rendering.
        content = (
            <FPSLink
                ariaLabel={navigation.name}
                className={css(className, "navigation-element navigation-link focus-treatment flex-row flex-grow flex-center scroll-hidden relative")}
                href={navigation.href}
                id={navigation.id + "-link"}
                role="menuitem"
                key={navigation.id}
                onClick={() => {
                    this.expandedHubGroup = !hubGroup && navigation.parentId ? navigation.parentId : navigation.id;
                }}
                onTouchEnd={hubGroup && navigation.children!.length > 1 ? (e) => this.toggleExpandedHubGroup(navigation.id, e, true) : undefined}
                onTouchMove={this.onTouchMove}
                onTouchStart={this.onTouchStart}
                navigationId={hubGroup ? navigation.children![0].id : navigation.id}
                telemetrySource="vertical.hub"
                tabIndex={addTabStop ? 0 : undefined}
                removeUnderline={true}
            >
                {content}
                <span className="navigation-text expanded-only text-ellipsis flex-grow">{navigation.name}</span>
                {expandIcon}
            </FPSLink>
        );

        if (hubGroup) {
            if (navigation.children!.length > 1) {
                // Add the contextual menu if there are multiple children not shown.
                if (!showChildren || navigation.id !== this.displayedHubGroupId) {
                    content = (
                        <ExpandableContainer
                            className="flex-row flex-noshrink"
                            collapseDelay={90}
                            expandKey={KeyCode.rightArrow}
                            excludeFocusZone={true}
                            excludeTabStop={true}
                            expandableId={navigation.id}
                            expandDelay={100}
                            expandOnHover={true}
                            id={navigation.id + "-container"}
                            renderCallout={(expandable: IExpandable, id: string, element: HTMLElement) => (
                                <ContextualMenu
                                    anchorElement={element}
                                    anchorOrigin={{ horizontal: Location.end, vertical: Location.start }}
                                    menuProps={this.getMenuProps(navigation)}
                                    menuOrigin={{ horizontal: Location.start, vertical: Location.start }}
                                    onDismiss={expandable.collapse}
                                />
                            )}
                            role="menu"
                        >
                            {content}
                        </ExpandableContainer>
                    );
                }

                // Add the hub's if they should be shown under the hub group..
                if (showChildren) {
                    content = (
                        <React.Fragment>
                            {content}
                            {this.hubs.map((hub: IContributedNavigation) => this.renderNavigation(hub, false, false))}
                        </React.Fragment>
                    );
                }
            }

            // Wrap the hubgroup in a container, this allows us to place the proper highlight on the left.
            content = (
                <div
                    key={navigation.id}
                    className={css(
                        "hub-group-container flex-column flex-noshrink relative",
                        navigation.id === this.expandedHubGroup && "expanded-container",
                        navigation.id === this.displayedHubGroupId && "displayed-container",
                        (!showChildren || navigation.children!.length <= 1) && "hub-group-only"
                    )}
                >
                    {content}
                </div>
            );
        }

        return content;
    }

    private sortNavigationList(navigationList: IContributedNavigation[]): IContributedNavigation[] {
        const nonBuiltInNavigation = [];
        const builtInNavigation = [];

        for (const navigation of navigationList) {
            if (isNavigationItemBuiltinOrTrusted(navigation)) {
                builtInNavigation.push(navigation);
            } else {
                // If a hub does not have an order specified in the manifest, it will get a default
                // order value of 0 when we deserialize the contribution.
                // Instead, we want those hubs to get a default order value of 100.
                if (navigation.order === 0) {
                    navigation.order = Constants.defaultHubOrder;
                }

                nonBuiltInNavigation.push(navigation);
            }
        }

        builtInNavigation.sort(this.navigationComparer);
        nonBuiltInNavigation.sort(this.navigationComparer);

        return [...builtInNavigation, ...nonBuiltInNavigation];
    }

    private toggleExpansion = () => {
        const navService = this.context.pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
        navService.setNavigationDisplayMode(
            this.expanded.value === NavigationDisplayMode.Expanded ? NavigationDisplayMode.Collapsed : NavigationDisplayMode.Expanded
        );

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("WebPlatform", "VerticalNavigation", {
            action: "vertical-nav-expanded",
            displayMode: this.expanded.value,
            hubsAreVisible: this.showHubs.value
        });
    };

    private toggleExpandedHubGroup = (
        navigationId: string,
        event: React.TouchEvent<HTMLAnchorElement> | React.MouseEvent<HTMLElement>,
        isTouch?: boolean
    ) => {
        event.preventDefault();
        // touchMove events are sensitive, so a small buffer is needed. 5 events seems to be a good buffer but can be adjusted if needed.
        if (this.touchMoveCounter > 5) {
            return;
        }

        if (isTouch) {
            this.showHubs.value = true;
        }

        if (this.expandedHubGroup === navigationId) {
            this.expandedHubGroup = "";
            this.forceUpdate();
            return;
        }
        this.expandedHubGroup = navigationId;
        const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        this.buildNavigationLists(navigationService, this.displayedHubGroupCollectionId, this.expandedHubGroup);
        this.forceUpdate();
    };

    private onTouchStart = () => {
        this.touchMoveCounter = 0;
    };

    private onTouchMove = () => {
        this.touchMoveCounter++;
    };
}

interface IProjectNavigationCalloutProps {
    collapse?: boolean;
    closeCallout: () => void;
}

export class ProjectNavigationCallout extends VssComponent<IProjectNavigationCalloutProps> {
    private isCollapsing: IObservableValue<boolean>;
    private pageHasFooter?: boolean;

    constructor(props: IProjectNavigationCalloutProps, context: IVssComponentContext) {
        super(props);
        this.isCollapsing = new ObservableValue(false);

        const layoutManager = context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        const footerComponents = layoutManager.getComponentsForRegion("footer");

        this.pageHasFooter = footerComponents && footerComponents.length > 0;
    }

    public render() {
        return (
            <Observer isCollapsing={this.isCollapsing}>
                {(props: { isCollapsing: boolean }) => {
                    return (
                        <Callout
                            anchorElement={document.body}
                            anchorPoint={{ x: 0, y: 0 }}
                            calloutOrigin={{ horizontal: Location.start, vertical: Location.start }}
                            className={css("navigation-callout scroll-hidden", this.pageHasFooter && "pageHasFooter")}
                            contentClassName="flex-column"
                            lightDismiss={true}
                            modal={true}
                            onDismiss={this.collapseCallout}
                        >
                            <div
                                className={css("nav-animation-container flex-row flex-grow scroll-hidden", props.isCollapsing && "collapsing")}
                                onAnimationEnd={this.onAnimationEnd}
                            >
                                <ProjectNavigation isCallout={true} onToggleExpand={this.collapseCallout} collapse={this.props.collapse} />
                            </div>
                        </Callout>
                    );
                }}
            </Observer>
        );
    }

    private collapseCallout = () => {
        this.isCollapsing.value = true;
    };

    private onAnimationEnd = () => {
        if (this.isCollapsing.value) {
            this.props.closeCallout();
        }
    };
}

const ProjectNavigationWrapper = (props: {}): JSX.Element => {
    const showPanel = new ObservableValue(false);
    const screenContext = React.useContext(ScreenContext);
    const pageContext = useComponentContext().pageContext;

    const togglePanel = (): void => {
        showPanel.value = !showPanel.value;
    };
    return (
        <Observer showPanel={showPanel} size={screenContext.size}>
            {(props: { showPanel: boolean; size: ScreenSize }) => {
                return (
                    !useFlyoutNavigation(pageContext, props.size) && (
                        <div className={"navigation-container flex-column"}>
                            {props.showPanel ? (
                                <>
                                    <div
                                        className="project-navigation region-navigation placeholder flex-column flex-grow flex-noshrink"
                                        role="presentation"
                                    />
                                    <ProjectNavigationCallout collapse={false} closeCallout={togglePanel} />
                                </>
                            ) : (
                                <ProjectNavigation
                                    onToggleExpand={props.size < ScreenSize.medium ? togglePanel : undefined}
                                    collapse={props.size < ScreenSize.medium ? true : undefined}
                                />
                            )}
                        </div>
                    )
                );
            }}
        </Observer>
    );
};

VssComponent.register("ms.vss-tfs-web.frame.project-navigation", ProjectNavigationWrapper);
